import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import slugify, { Options } from '@sindresorhus/slugify';
import { injectable } from 'inversify';
import isObject from 'lodash/isObject';

/* eslint-disable no-magic-numbers */

interface UserNames {
  first_name?: string;
  second_name?: string;
  middle_name?: string;
}

interface UserFormattedNames {
  firstName?: string;
  secondName?: string;
  middleName?: string;
}

const isUserFormattedNames = (
  user: UserNames | UserFormattedNames,
): user is UserFormattedNames =>
  'firstName' in user || 'secondName' in user || 'middleName' in user;

@injectable()
export class FormatService {
  /**
   * Преобразовать дату к формату "ДД.ММ.ГГГГ в ЧЧ:ММ"
   * Если строковая дата не валидна/не корректна, то возвращается пустая строка
   *
   * @param dateString - Дата
   * @returns Строка в формате "ДД.ММ.ГГГГ в ЧЧ:ММ"
   */
  toDateTime(dateString: string): string {
    if (Number.isNaN(Date.parse(dateString))) {
      return '';
    }

    const date = new Date(dateString);

    return `${date.getDate().toString().padStart(2, '0')}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}.${date.getFullYear()} в ${date
      .getHours()
      .toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  /**
   * Преобразовать дату к формату "ДД.ММ.ГГГГ"
   * Если строковая дата не валидна/не корректна, то возвращается пустая строка
   *
   * @param dateString - Дата
   * @returns Строка в формате "ДД.ММ.ГГГГ"
   *  */

  toDateFullMonthTime(dateString: string): string {
    if (Number.isNaN(Date.parse(dateString))) {
      return '';
    }

    const date = new Date(dateString);

    return `${date.toLocaleDateString('ru-RU', {
      month: 'long',
      day: 'numeric',
    })} ${date.getFullYear()} ${this.toTime(dateString)}`;
  }

  /** Преобразовать дату к формату "ДД.ММ.ГГГГ"
   * Если строковая дата не валидна/не корректна, то возвращается пустая строка
   *
   * @param dateString - Дата
   * @returns Строка в формате "ДД.ММ.ГГГГ"
   */
  toDate(dateString: string): string {
    if (Number.isNaN(Date.parse(dateString))) {
      return '';
    }

    const date = new Date(dateString);

    return `${date.getDate().toString().padStart(2, '0')}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}.${date.getFullYear()}`;
  }

  /**
   * Преобразовать дату к формату с "yyyy-MM-dd"
   * Если строковая дата не валидна/не корректна, то возвращается пустая строка
   *
   * @param date - Дата
   * @returns Строка с датой
   */
  toISODate(date: string | Date): string {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return '';
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Преобразовать дату к формату времени "ЧЧ:ММ"
   * Если строковая дата не валидна/не корректна, то возвращается пустая строка
   *
   * @param dateString - Дата
   * @returns Строка в формате "ЧЧ:ММ"
   */
  toTime(dateString: string): string {
    if (Number.isNaN(Date.parse(dateString))) {
      return '';
    }

    const date = new Date(dateString);

    return `${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  /**
   * Преобразовать число к строке
   * Знак сохраняется
   * Если число меньше 10, то после знака добавляется 0
   *
   * @param count - Преобразуемое число
   * @returns Строка с нулевым заполнением
   */
  zeroPadding(count: number): string {
    const absoluteCount = Math.abs(count);

    const minus = count < 0 ? '-' : '';

    const zero = absoluteCount < 10 ? '0' : '';

    return `${minus}${zero}${absoluteCount}`;
  }

  /**
   * Получить выражение, описывающее множественное число, состоящее из числа и слова в правильной форме
   *
   * @param count - Числовая часть
   * @param words - Словесная часть
   * @param options - Параметры
   * @returns Строка с числом и словом в правильной форме
   */
  pluralize = (
    count: number,
    words: string[],
    options: { withZeroPad?: boolean; hideNumber?: boolean } = {},
  ) => {
    const { withZeroPad, hideNumber } = options;
    const cases = [2, 0, 1, 1, 1, 2];
    const formattedCount = Math.abs(count);

    const resultNumber = withZeroPad ? this.zeroPadding(count) : count;

    if (formattedCount - Math.floor(formattedCount)) {
      return hideNumber ? words[1] : `${resultNumber} ${words[1]}`;
    }

    const word =
      words[
        formattedCount % 100 > 4 && formattedCount % 100 < 20
          ? 2
          : cases[Math.min(formattedCount % 10, 5)]
      ];

    return hideNumber ? word : `${resultNumber} ${word}`;
  };

  /**
   * Преобразовать количество байтов к ближайшему целому значению единицы более высокого порядка
   *
   * @param bytes - Количество байтов
   * @returns Строка с размером
   */
  bytesToSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes == 0) return '0 Byte';
    const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))));
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
  };

  /**
   * Преобразовать номер телефона к формату "+7 999 999 99 99"
   *
   * @param phoneString - Номер телефона
   * @returns Отформатированный номер телефона
   */
  toPhone(phoneString: string): string {
    const phoneRegExp = new RegExp(
      '(\\+?[7|8])(\\d{3})(\\d{3})(\\d{2})(\\d{2})',
    );

    const phoneMatch = phoneRegExp.exec(phoneString);

    if (!phoneMatch) {
      return phoneString;
    }

    return `+7 ${phoneMatch[2]} ${phoneMatch[3]} ${phoneMatch[4]} ${phoneMatch[5]}`;
  }

  /**
   * Преобразовать номер СНИЛС к формату "999-999-999 99"
   *
   * @param snilsString - Номер СНИЛС
   * @returns Отформатированный номер СНИЛС
   */
  toSnils(snilsString: string): string {
    return snilsString.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/i, '$1-$2-$3 $4');
  }

  /**
   * Преобразовать число к сокращенной записе с указанием тыс/млн/млрд
   *
   * @param rawNumber - Число
   * @returns Сокращенная запись числа
   */
  convertNumber(rawNumber: number): string {
    let num = Math.abs(rawNumber);
    let rank = 0;

    while (num >= 1000) {
      rank++;
      num = +(num / 1000).toFixed(1);
    }

    const result = num.toString().replace('.', ',');

    switch (rank) {
      case 1:
        return `${result} тыс.`;
      case 2:
        return `${result} млн.`;
      case 3:
        return `${result} млрд.`;
      default:
        return result;
    }
  }

  /**
   * Преобразовать дату к формату с GMT "dd.MM.yyyy HH:mm (zzzz)"
   *
   * @param date - Дата
   * @returns Строка с датой и часовым поясом
   */
  toTimeStringWithGMT(date: string) {
    return format(parseISO(date), 'dd.MM.yyyy HH:mm (zzzz)');
  }

  /**
   * Преобразовать дату к формату с GMT "dd.MM.yyyy"
   *
   * @param date - Дата
   * @returns Строка с датой
   */
  toTimeString(date: string) {
    return format(parseISO(date), 'dd.MM.yyyy');
  }

  /**
   * Преобразует переданное значение в булевый тип.
   *
   * @param value - Значение для преобразования
   * @returns Результат преобразования в булевый тип
   */
  toBoolean(value: unknown) {
    if (value) {
      if (value === 'true' || value === 'false') {
        return value === 'true';
      } else {
        return !!value;
      }
    } else {
      return false;
    }
  }

  /**
   * Преобразовать диапазон дат в строку с количеством дней между ними
   *
   * @param dateFrom - Дата начала
   * @param dateTo - Дата окончания
   * @returns Строка вида "ДД.ММ.ГГГГ - ДД.ММ.ГГГГ (N дн.)"
   */
  toDateRangeWithDaysBetween(dateFrom: string, dateTo: string) {
    const dateStart = this.toDate(dateFrom);
    const dateEnd = this.toDate(dateTo);

    return `${dateStart} - ${dateEnd} (${differenceInCalendarDays(
      new Date(dateTo),
      new Date(dateFrom),
    )} дн.)`;
  }

  /**
   * Преобразовать диапазон дат в строку с количеством дней между ними (включительно)
   *
   * @param dateFrom - Дата начала
   * @param dateTo - Дата окончания
   * @param divider - Разделитель перед количеством дней; 'bracket' оборачивает его в скобки
   * @returns Строка вида "ДД.ММ.ГГГГ - ДД.ММ.ГГГГ (N дн.)" или "ДД.ММ.ГГГГ - ДД.ММ.ГГГГ • N дн." если divider === '• '
   */
  toDateRangeWithDaysBetweenInclusive(
    dateFrom: string,
    dateTo: string,
    divider: string = 'bracket',
  ) {
    const dateStart = this.toDate(dateFrom);
    const dateEnd = this.toDate(dateTo);

    return `${dateStart} - ${dateEnd} ${divider === 'bracket' ? '(' : divider}${
      differenceInCalendarDays(new Date(dateTo), new Date(dateFrom)) + 1
    } дн.${divider === 'bracket' ? ')' : ''}`;
  }

  /**
   * Преобразовать диапазон дат в строку
   *
   * @param dateFrom - Дата начала
   * @param dateTo - Дата окончания
   * @returns Строка вида "ДД.ММ.ГГГГ - ДД.ММ.ГГГГ"
   */
  toDateRange(dateFrom: string, dateTo: string) {
    const dateStart = this.toDate(dateFrom);
    const dateEnd = this.toDate(dateTo);

    return `${dateStart} - ${dateEnd}`;
  }

  /**
   * Преобразовать ФИО в краткий формат
   *
   * @param firstName - Имя
   * @param middleName - Отчество
   * @param secondName - Фамилия
   * @returns Строка вида "Фамилия И. О."
   */
  toShortName(
    firstName: string,
    middleName: string,
    secondName: string,
  ): string {
    return `${secondName} ${firstName[0]}.${
      middleName ? ` ${middleName[0]}.` : ''
    }`;
  }

  /**
   * Преобразовать Ф.И.О. в единую строку"
   *
   * @param namesArr - Значение для преобразования. Порядок имеет значение.
   * @returns Результат преобразования.
   *  */
  toFullName(namesArr: string[]): string {
    return namesArr.filter(Boolean).join(' ');
  }

  /**
   * Преобразует переданное значение в формат для url.
   *
   * @param text - Значение для преобразования.
   * @param options - Опции для библиотеки.
   * @returns Результат преобразования.
   */
  textToSlug(text: string, options?: Options): string {
    return slugify(text, options);
  }

  cleanUrlPath(param: string, maxLength = 100) {
    return encodeURIComponent(
      param.substring(0, maxLength).replace(/[^a-zA-Zа-яА-ЯёЁ0-9/-_]/g, ''),
    );
  }

  /**
   * Очистить параметр запроса от недопустимых символов
   *
   * @param param - Параметр запроса
   * @param maxLength - Максимальная длина параметра
   * @returns Очищенный параметр
   */
  cleanSingleQueryParam(param: string, maxLength = 100) {
    return encodeURIComponent(
      param.substring(0, maxLength).replace(/[^a-zA-Zа-яА-ЯёЁ0-9-_]/g, ''),
    );
  }

  /**
   * удаляются непечатные символы, нестандартные пробелы из форматирования заменяются на дефолтный пробел и выполняем нормализацию Unicode-строк используя форму NFKC (совместимая композиция) (Normalization Form KC - Compatibility Composition)
   *
   * @param text - строка с непечатными символами
   * @returns Очищенный параметр
   */
  sanitizeText(text: string) {
    return text
      .replace(
        /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF\u2028-\u2029\u200E-\u200F]/g,
        '',
      )
      .replace(/[\u00A0\u1680\u180E\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
      .replace(/\u00AD/g, '\n')
      .normalize('NFKC');
  }

  /**
   * Очистить параметры запроса от недопустимых символов
   *
   * @param query - Объект с параметрами запроса
   * @returns Очищенный объект параметров
   */
  cleanQueryParams(
    query: Record<string, unknown>,
  ): Record<string, string | (string | number)[] | number> {
    if (!query || Array.isArray(query) || !isObject(query)) {
      return {};
    }

    const initialResult: Record<string, string | (string | number)[] | number> =
      {};

    return Object.keys(query).reduce((result, param) => {
      if (typeof query[param] === 'number') {
        return { ...result, [param]: query[param] };
      }

      if (typeof query[param] === 'string') {
        return { ...result, [param]: this.cleanSingleQueryParam(query[param]) };
      }

      if (Array.isArray(query[param])) {
        const resultArray: (number | string)[] = [];

        query[param]
          .map((value) => {
            if (typeof value === 'number') {
              return value;
            }
            if (typeof value === 'string') {
              return this.cleanSingleQueryParam(value);
            }
            return null;
          })
          .filter((value) => value !== null)
          .forEach((value) => resultArray.push(value));

        if (resultArray.length) {
          return {
            ...result,
            [param]: resultArray,
          };
        }
      }

      return result;
    }, initialResult);
  }

  /**
   * Сформировать инициалы пользователя
   *
   * @param user - объект, который содержит поля firstName или first_name и secondName или second_name
   * @returns Инициалы через пробел
   */
  getUserInitials(user: UserNames | UserFormattedNames | undefined = {}) {
    const secondName = isUserFormattedNames(user)
      ? user.secondName
      : user.second_name;

    const firstName = isUserFormattedNames(user)
      ? user.firstName
      : user.first_name;

    return [secondName?.charAt(0), firstName?.charAt(0)]
      .filter(Boolean)
      .join('');
  }
}
